import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineAdjustmentsHorizontal,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentCheck,
  HiOutlineSquares2X2,
  HiOutlineBars3,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import {
  authService,
  capaService,
  rcaService,
  masterDataService,
  userService,
  safetyObservationService,
  plantService,
  lineService,
} from "../services";
import ObservationPreview from "./SafetyBehaviorObservation/schedule/ObservationPreview";
import { usePermissions } from "../hooks/usePermissions";
import Pagination from "../components/Pagination";
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import { toast } from "react-toastify";
import "./CAPA.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedCAPAData = () => {
  try {
    const cachedCapaList = sessionStorage.getItem("capa_records_data");
    const cachedRcaRecords = sessionStorage.getItem("rca_records_data");
    const cachedStatuses = sessionStorage.getItem("capa_statuses_data");
    const cachedTaskStatuses = sessionStorage.getItem(
      "capa_task_statuses_data",
    );
    const cachedUsers = sessionStorage.getItem("users_data");

    return {
      capaList: cachedCapaList ? JSON.parse(cachedCapaList) : [],
      rcaRecords: cachedRcaRecords ? JSON.parse(cachedRcaRecords) : [],
      statuses: cachedStatuses ? JSON.parse(cachedStatuses) : [],
      taskStatuses: cachedTaskStatuses ? JSON.parse(cachedTaskStatuses) : [],
      users: cachedUsers ? JSON.parse(cachedUsers) : [],
      hasAll: !!(
        cachedCapaList &&
        cachedRcaRecords &&
        cachedStatuses &&
        cachedTaskStatuses &&
        cachedUsers
      ),
    };
  } catch (e) {
    return {
      capaList: [],
      rcaRecords: [],
      statuses: [],
      taskStatuses: [],
      users: [],
      hasAll: false,
    };
  }
};

const CAPA = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks for CAPA
  const canView = hasPermission("CAPA.View");
  const canCreate = hasPermission("CAPA.Create");
  const canEdit = hasPermission("CAPA.Edit");
  const canDelete = hasPermission("CAPA.Delete");

  // Permission checks for CAPA Actions
  const canCreateAction = hasPermission("CAPAAction.Create");
  const canEditAction = hasPermission("CAPAAction.Edit");
  const canDeleteAction = hasPermission("CAPAAction.Delete");

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedCAPAData();
  const [capaList, setCapaList] = useState(cachedData.capaList);
  const [rcaRecords, setRcaRecords] = useState(cachedData.rcaRecords);
  const [statuses, setStatuses] = useState(cachedData.statuses);
  const [taskStatuses, setTaskStatuses] = useState(cachedData.taskStatuses);
  const [users, setUsers] = useState(cachedData.users);
  const [capaTasks, setCapaTasks] = useState({}); // { capaId: [tasks] }
  const [capaTaskCounts, setCapaTaskCounts] = useState({}); // { capaId: count }
  const [expandedCapa, setExpandedCapa] = useState(null); // capaId that is expanded

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [isSboModalOpen, setIsSboModalOpen] = useState(false);
  const [selectedSboRoundId, setSelectedSboRoundId] = useState(null);
  const [selectedCapaId, setSelectedCapaId] = useState(null);

  // Helper to handle SBO badge click — looks up round by SBO number if roundId is not stored on CAPA
  const handleSboClick = async (capa) => {
    const sboNum =
      capa.observationNumber ||
      (capa.description && capa.description.match(/SBO-\d{4}-\d+/)?.[0]);

    // If the CAPA record has a direct round ID, use it
    if (capa.observationRoundId || capa.roundId) {
      setSelectedSboRoundId(capa.observationRoundId || capa.roundId);
      setSelectedCapaId(capa.capaId);
      setIsSboModalOpen(true);
      return;
    }

    // Otherwise, look up the observation round by its SBO number
    if (sboNum) {
      try {
        const result =
          await safetyObservationService.getSafetyObservationRounds({
            searchTerm: sboNum,
            pageSize: 1,
          });
        const rounds = result?.data || result || [];
        if (rounds.length > 0) {
          setSelectedSboRoundId(rounds[0].roundId);
          setSelectedCapaId(capa.capaId);
          setIsSboModalOpen(true);
        } else {
          console.warn("No observation round found for SBO number:", sboNum);
        }
      } catch (err) {
        console.error("Error looking up SBO round:", err);
      }
    }
  };
  const [selectedCapaForTask, setSelectedCapaForTask] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    taskDescription: "",
    assignedTo: "",
    dueDate: "",
    statusId: "",
    notes: "",
    priority: 3,
  });
  const [taskFormErrors, setTaskFormErrors] = useState({});
  const [submittingTask, setSubmittingTask] = useState(false);

  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    statusId: "",
    module: "",
    plantId: "",
  });
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewMode, setViewMode] = useState("list");

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedCAPAData();
      if (refreshedCachedData.hasAll) {
        setCapaList(refreshedCachedData.capaList);
        setRcaRecords(refreshedCachedData.rcaRecords);
        setStatuses(refreshedCachedData.statuses);
        setTaskStatuses(refreshedCachedData.taskStatuses);
        setUsers(refreshedCachedData.users);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      const msg = "Please login to manage CAPA records";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    // Check if we have cached data - if yes, show page immediately
    const hasCachedData =
      sessionStorage.getItem("capa_records_data") &&
      sessionStorage.getItem("rca_records_data") &&
      sessionStorage.getItem("capa_statuses_data") &&
      sessionStorage.getItem("capa_task_statuses_data") &&
      sessionStorage.getItem("users_data");

    if (!canView) {
      const msg = "You do not have permission to view CAPA records";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (hasCachedData) {
      setLoading(false);
      loadInitialData();
      return;
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("capa_records_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const paginationParams = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      const [capaResponse, rcaData, statusesData, usersData, plantsData, linesData] =
        await Promise.all([
          capaService.getCapaRecords(null, null, null, paginationParams),
          rcaService.getRcaRecords(),
          masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
          userService.getAllUsers?.() || Promise.resolve([]),
          plantService.getAllPlants(),
          lineService.getAllLines(),
        ]);

      setPlants(plantsData || []);
      setLines(linesData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
          setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      if (capaResponse && capaResponse.data) {
        setCapaList(capaResponse.data || []);
        setPagination((prev) => ({
          ...prev,
          totalCount: capaResponse.pagination?.totalCount || 0,
          totalPages: capaResponse.pagination?.totalPages || 0,
        }));
      } else {
        // Fallback for old API response format
        setCapaList(capaResponse || []);
      }
      setRcaRecords(rcaData || []);
      setUsers(usersData || []);

      const allStatuses = Array.isArray(statusesData) ? statusesData : [];

      // Filter CAPA statuses - Show CAPA statuses
      const normalizeName = (name) =>
        name ? name.toLowerCase().replace(/\s/g, "") : "";

      const deduplicateStatuses = (statusList) => {
        const uniqueStatuses = [];
        const seenNames = new Set();

        statusList.forEach((status) => {
          const key = normalizeName(status.statusName);
          if (!seenNames.has(key)) {
            uniqueStatuses.push(status);
            seenNames.add(key);
          }
        });

        return uniqueStatuses;
      };

      // Filter CAPA statuses - Show CAPA statuses
      let capaStatuses = allStatuses.filter((s) => s.statusType === "CAPA");
      capaStatuses = deduplicateStatuses(capaStatuses);
      setStatuses(
        capaStatuses.length > 0
          ? capaStatuses
          : deduplicateStatuses(allStatuses),
      );

      // Filter Action statuses for tasks
      let actionStatuses = allStatuses.filter(
        (s) =>
          !s.statusType ||
          s.statusType === "Action" ||
          s.statusType === null ||
          s.statusType === undefined,
      );
      actionStatuses = deduplicateStatuses(actionStatuses);
      setTaskStatuses(
        actionStatuses.length > 0
          ? actionStatuses
          : deduplicateStatuses(allStatuses),
      );

      // Cache the data
      const capaData = capaResponse?.data || capaResponse || [];
      sessionStorage.setItem("capa_records_data", JSON.stringify(capaData));
      sessionStorage.setItem("rca_records_data", JSON.stringify(rcaData || []));
      const uniqueAllStatuses = deduplicateStatuses(allStatuses);

      sessionStorage.setItem(
        "capa_statuses_data",
        JSON.stringify(
          capaStatuses.length > 0 ? capaStatuses : uniqueAllStatuses,
        ),
      );
      sessionStorage.setItem(
        "capa_task_statuses_data",
        JSON.stringify(
          actionStatuses.length > 0 ? actionStatuses : uniqueAllStatuses,
        ),
      );
      sessionStorage.setItem("users_data", JSON.stringify(usersData || []));

      // Load task counts for all CAPAs in background
      if (capaData && capaData.length > 0) {
        const taskCounts = {};
        // Don't await this - let it run in background to unblock UI
        Promise.all(
          capaData.map(async (capa) => {
            try {
              const tasks = await capaService.getCapaActionsByCapa(capa.capaId);
              taskCounts[capa.capaId] = tasks?.length || 0;
            } catch (err) {
              console.error(
                `Error loading tasks for CAPA ${capa.capaId}:`,
                err,
              );
              taskCounts[capa.capaId] = 0;
            }
          }),
        ).then(() => {
          setCapaTaskCounts(taskCounts);
        });
      }
    } catch (err) {
      console.error("Error loading CAPA records:", err);
      const msg = "Failed to load CAPA records. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchCapaWithFilters = async (page = pagination.currentPage) => {
    try {
      setRefreshing(true);
      setError("");

      const paginationParams = {
        page: page,
        pageSize: pagination.pageSize,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      const response = await capaService.getCapaRecords(
        filters.rcaId ? parseInt(filters.rcaId, 10) : null,
        filters.statusId ? parseInt(filters.statusId, 10) : null,
        null,
        paginationParams,
      );

      if (response && response.data) {
        setCapaList(response.data || []);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalCount: response.pagination?.totalCount || 0,
          totalPages: response.pagination?.totalPages || 0,
        }));

        // Load task counts for filtered CAPAs in background
        if (response.data && response.data.length > 0) {
          const taskCounts = {};
          // Don't await this - let it run in background
          Promise.all(
            response.data.map(async (capa) => {
              try {
                const tasks = await capaService.getCapaActionsByCapa(
                  capa.capaId,
                );
                taskCounts[capa.capaId] = tasks?.length || 0;
              } catch (err) {
                // Silently handle errors for task counts - don't show popup
                console.debug(
                  `Error loading tasks for CAPA ${capa.capaId}:`,
                  err,
                );
                taskCounts[capa.capaId] = 0;
              }
            }),
          ).then(() => {
            setCapaTaskCounts(taskCounts);
          });
        }
      } else {
        // Fallback for old API response format
        setCapaList(response || []);
      }
    } catch (err) {
      console.error("Error fetching CAPA records:", err);
      setError("Failed to fetch CAPA records. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    // Close filter drawer immediately before fetching
    setShowFilterDrawer(false);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Use setTimeout to ensure drawer closes before fetch starts
    setTimeout(() => {
      fetchCapaWithFilters(1);
    }, 0);
  };

  const clearFilters = () => {
    setFilters({
      statusId: "",
      module: "",
      plantId: "",
    });
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchCapaWithFilters(1);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchCapaWithFilters(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPagination((prev) => ({ ...prev, pageSize, currentPage: 1 }));
    fetchCapaWithFilters(1);
  };

  const getRcaTitle = (rcaId) => {
    if (!rcaId) return "-";
    const rca = rcaRecords.find((r) => r.rcaId === rcaId);
    return rca ? rca.problemDesc || `RCA ${rcaId}` : `RCA ${rcaId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const getTaskStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = taskStatuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const getUserName = (userId) => {
    if (!userId) return "-";
    const user = users.find((u) => u.userId === userId || u.id === userId);
    if (user) {
      return (
        user.fullName ||
        user.name ||
        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
        user.email ||
        `User ${userId}`
      );
    }
    return `User ${userId}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return d.toLocaleString();
  };

  const filteredCapaList = useMemo(() => {
    return capaList.filter((capa) => {
      const matchesSearch =
        !searchTerm ||
        (capa.title &&
          capa.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (capa.description &&
          capa.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        !filters.statusId || capa.statusId?.toString() === filters.statusId;

      let matchesModule = true;
      if (filters.module) {
        if (filters.module === "Safety Observation Module") {
          matchesModule =
            capa.description && capa.description.includes("Safety Observation");
        } else if (filters.module === "RCA Module") {
          matchesModule = !!capa.rcaId;
        } else if (filters.module === "Incident Management") {
          matchesModule =
            capa.description && capa.description.includes("Incident");
        } else if (filters.module === "5S Audits") {
          matchesModule =
            capa.description &&
            (capa.description.includes("5S") ||
              capa.description.includes("Audit"));
        } else if (filters.module === "OPL Module") {
          matchesModule =
            capa.description &&
            (capa.description.includes("OPL") ||
              capa.description.includes("One Point Lesson"));
        } else if (filters.module === "Kaizen Module") {
          matchesModule =
            capa.description && capa.description.includes("Kaizen");
        } else if (filters.module === "Work Order") {
          matchesModule =
            capa.description && capa.description.includes("Work Order");
        } else if (filters.module === "Work Permits") {
          matchesModule =
            capa.description && capa.description.includes("Permit");
        } else if (filters.module === "Enterprise ERP") {
          matchesModule = capa.description && capa.description.includes("ERP");
        } else if (filters.module === "CAPA Module") {
          // General CAPAs not linked to others specific keywords
          const otherKeywords = [
            "Safety Observation",
            "Incident",
            "5S",
            "Audit",
            "OPL",
            "Kaizen",
            "Work Order",
            "Permit",
            "ERP",
          ];
          matchesModule =
            !capa.rcaId &&
            (!capa.description ||
              !otherKeywords.some((k) => capa.description.includes(k)));
        }
      }

      let matchesPlant = true;
      if (filters.plantId) {
        const pid = filters.plantId;
        if (capa.plantId != null) {
          matchesPlant = capa.plantId.toString() === pid;
        } else if (capa.rcaId) {
          const rca = rcaRecords.find((r) => r.rcaId === capa.rcaId);
          matchesPlant = rca?.plantId?.toString() === pid;
        } else {
          // No plant on CAPA and no RCA — tenant-level / standalone; do not hide when a plant is selected
          matchesPlant = true;
        }
      }

      return matchesSearch && matchesStatus && matchesModule && matchesPlant;
    });
  }, [capaList, filters, searchTerm, rcaRecords]);

const handleUpdateCapaStatus = async (capaId, newStatusId) => {
    try {
      const toastId = toast.loading("Updating CAPA status...");
      const capa = capaList.find((c) => c.capaId === capaId);
      if (!capa) {
        toast.update(toastId, { render: "CAPA not found.", type: "error", isLoading: false, autoClose: 3000, closeButton: true });
        return;
      }

      const updatePayload = {
        rcaId: capa.rcaId,
        title: capa.title,
        description: capa.description,
        capaType: capa.capaType,
        assignedTo: capa.assignedTo,
        targetDate: capa.targetDate,
        statusId: newStatusId,
        completedDate: capa.completedDate,
        effectivenessReview: capa.effectivenessReview,
        reviewedBy: capa.reviewedBy,
        reviewedDate: capa.reviewedDate,
      };

      await capaService.updateCapaRecord(capaId, updatePayload);
      await fetchCapaWithFilters();
      toast.update(toastId, { render: "CAPA status updated successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error updating CAPA status:", err);
      const msg = "Failed to update CAPA status. Please try again.";
      setError(msg);
      toast.error(msg, { closeButton: true });
    }
  };

  const handleDelete = async (capa) => {
    if (!canDelete) {
      setError("You do not have permission to delete CAPA records");
      return;
    }
    try {
      const toastId = toast.loading("Deleting CAPA record...");
      await capaService.deleteCapaRecord(capa.capaId);
      await fetchCapaWithFilters();
      setDeleteConfirm(null);
      toast.update(toastId, { render: "CAPA record deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error deleting CAPA record:", err);
      const msg = "Failed to delete CAPA record. Please try again.";
      setError(msg);
      toast.error(msg, { closeButton: true });
    }
  };

  const loadTasksForCapa = async (capaId) => {
    try {
      const tasks = await capaService.getCapaActionsByCapa(capaId);
      setCapaTasks((prev) => ({ ...prev, [capaId]: tasks || [] }));
    } catch (err) {
      console.error("Error loading tasks for CAPA:", err);
    }
  };

  const toggleExpandCapa = (capaId) => {
    if (expandedCapa === capaId) {
      setExpandedCapa(null);
    } else {
      setExpandedCapa(capaId);
      if (!capaTasks[capaId]) {
        loadTasksForCapa(capaId);
      }
    }
  };

  const handleAddTaskClick = (capa) => {
    if (!canCreateAction) {
      setError("You do not have permission to create CAPA actions");
      return;
    }
    setSelectedCapaForTask(capa);
    setTaskFormData({
      taskDescription: "",
      assignedTo: "",
      dueDate: "",
      statusId: "",
      notes: "",
      priority: 3,
    });
    setTaskFormErrors({});
    setShowTaskModal(true);
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setTaskFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateTaskForm = () => {
    const errors = {};
    if (!taskFormData.taskDescription?.trim()) {
      errors.taskDescription = "Task description is required";
    }
    if (!taskFormData.statusId || taskFormData.statusId === "") {
      errors.statusId = "Status is required";
    }
    setTaskFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateTaskStatus = async (actionId, newStatusId) => {
    try {
      await capaService.updateCapaActionStatus(actionId, newStatusId);

      // Update local state
      setCapaTasks((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((capaId) => {
          updated[capaId] = updated[capaId].map((task) =>
            task.actionId === actionId
              ? { ...task, statusId: newStatusId }
              : task,
          );
        });
        return updated;
      });
      toast.success("Task status updated!");
    } catch (err) {
      console.error("Error updating task status:", err);
      const msg = "Failed to update task status. Please try again.";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleSaveTask = async () => {
    if (!canCreateAction) {
      setError("You do not have permission to create CAPA actions");
      return;
    }

    if (!validateTaskForm()) {
      return;
    }

    try {
      setSubmittingTask(true);
      setError("");

      const toastId = toast.loading("Creating sub-task...");
      const taskStatusIdNum = parseInt(taskFormData.statusId, 10);
      if (isNaN(taskStatusIdNum) || taskStatusIdNum <= 0) {
        setTaskFormErrors({ statusId: "Please select a valid status" });
        setSubmittingTask(false);
        toast.update(toastId, { render: "Please select a valid status", type: "error", isLoading: false, autoClose: 3000, closeButton: true });
        return;
      }

      const taskPayload = {
        capaId: selectedCapaForTask.capaId,
        taskDescription: taskFormData.taskDescription.trim(),
        assignedTo: taskFormData.assignedTo
          ? parseInt(taskFormData.assignedTo, 10)
          : null,
        dueDate: taskFormData.dueDate
          ? new Date(taskFormData.dueDate).toISOString()
          : null,
        statusId: taskStatusIdNum,
        notes: taskFormData.notes?.trim() || null,
        priority: parseInt(taskFormData.priority, 10),
      };

      await capaService.createCapaAction(taskPayload);

      // Reload tasks for this CAPA
      await loadTasksForCapa(selectedCapaForTask.capaId);

      // Automatically expand the CAPA section to show the new task
      setExpandedCapa(selectedCapaForTask.capaId);

      setShowTaskModal(false);
      setSelectedCapaForTask(null);
      setTaskFormData({
        taskDescription: "",
        assignedTo: "",
        dueDate: "",
        statusId: "",
        notes: "",
        priority: 3,
      });
      toast.update(toastId, { render: "Sub-task created successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error saving task:", err);
      const msg = err.response?.data?.message || "Failed to save task";
      setError(msg);
      toast.error(msg, { closeButton: true });
    } finally {
      setSubmittingTask(false);
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1:
        return "High";
      case 2:
        return "Medium";
      case 3:
        return "Low";
      default:
        return "Low";
    }
  };
  if (loading) {
    return <PageLoading message="Loading CAPA records..." />;
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Corrective & Preventive Actions (CAPA)</h1>
          <p>
            Manage corrective and preventive actions generated from Root Cause
            Analysis
          </p>
        </div>
        <div className="header-actions">
          {canCreate && (
            <button
              className="btn-primary"
              onClick={() => navigate("/home/capa-entry")}
            >
              <HiOutlinePlus />
              New CAPA
            </button>
          )}
          <button
            className="btn-secondary"
            onClick={clearFilters}
            disabled={refreshing}
          >
            <HiOutlineClipboardDocumentCheck
              className={refreshing ? "spinning" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 ">
        <div className="table-header kizantable flex-row">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search title, description..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="filters-row justify-content-end">
            <div className="view-toggle-group">
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                type="button"
                onClick={() => setViewMode("list")}
                title="List View"
              >
                <HiOutlineBars3 />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "card" ? "active" : ""}`}
                type="button"
                onClick={() => setViewMode("card")}
                title="Card View"
              >
                <HiOutlineSquares2X2 />
              </button>
            </div>
            <button
              className="filter-toggle"
              type="button"
              onClick={() => setShowFilterDrawer(true)}
            >
              <HiOutlineAdjustmentsHorizontal />
              <span>Filters</span>
            </button>
            <div className="table-info">
              {filteredCapaList.length}{" "}
              {filteredCapaList.length === 1 ? "record" : "records"}
            </div>
          </div>
        </div>

        {filteredCapaList.length === 0 ? (
          <div className="empty-state">
            <h3>No CAPA records found</h3>
            <p>
              {searchTerm || filters.statusId || filters.rcaId
                ? "Try adjusting your filters or search terms"
                : canCreate
                  ? "Start by creating your first CAPA record from an RCA"
                  : "No CAPA records found."}
            </p>
            {canCreate && (
              <button
                className="btn-primary"
                onClick={() => navigate("/home/capa-entry")}
              >
                <HiOutlinePlus />
                Create First CAPA
              </button>
            )}
          </div>
        ) : viewMode === "list" ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>Title</span>
                  </div>
                </th>
                <th>RCA</th>
                <th>Type</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Target Date</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {filteredCapaList.map((capa) => {
                return (
                  <React.Fragment key={capa.capaId}>
                    <tr>
                      <td>
                        <div className="action-buttons">
                          {canEdit && (
                            <button
                              className="btn-icon-edit"
                              title="Open CAPA"
                              onClick={() =>
                                navigate(`/home/capa-entry/${capa.capaId}`)
                              }
                            >
                              <HiOutlinePencil />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn-icon-delete"
                              title="Delete CAPA"
                              onClick={() => setDeleteConfirm(capa)}
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{ cursor: "pointer", flex: 1 }}
                            onClick={() => toggleExpandCapa(capa.capaId)}
                            title="Click to view tasks"
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <span>{capa.title || "-"}</span>
                              {(capa.observationNumber ||
                                (capa.description &&
                                  capa.description.match(/SBO-\d{4}-\d+/))) && (
                                  <span
                                    className="sbo-link-badge"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSboClick(capa);
                                    }}
                                  >
                                    <HiOutlineShieldCheck size={12} />
                                    {capa.observationNumber ||
                                      (capa.description &&
                                        capa.description.match(
                                          /SBO-\d{4}-\d+/,
                                        )?.[0])}
                                  </span>
                                )}
                            </div>
                          </span>
                          {canCreateAction && (
                            <button
                              className="btn-icon-add-task"
                              title="Add Task"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddTaskClick(capa);
                              }}
                            >
                              <HiOutlinePlus />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        {capa.rcaId ? (
                          <button
                            className="link-button"
                            onClick={() =>
                              navigate(`/home/rca-entry/${capa.rcaId}`)
                            }
                          >
                            {getRcaTitle(capa.rcaId)}
                          </button>
                        ) : capa.observationNumber ||
                          (capa.description &&
                            (capa.description.includes("Safety Observation") ||
                              capa.description.match(/SBO-\d{4}-\d+/))) ? (
                          <div
                            className="sbo-source-indicator"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleSboClick(capa);
                            }}
                          >
                            <HiOutlineShieldCheck className="text-primary" />
                            <span>SBO</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span className="type-pill">
                          {capa.capaType || "Corrective"}
                        </span>
                      </td>
                      <td>
                        <select
                          className="status-select-inline"
                          value={capa.statusId || ""}
                          onChange={(e) =>
                            handleUpdateCapaStatus(
                              capa.capaId,
                              parseInt(e.target.value, 10),
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          {statuses.map((status) => (
                            <option
                              key={status.statusId}
                              value={status.statusId}
                            >
                              {status.statusName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {capa.assignedTo ? getUserName(capa.assignedTo) : "-"}
                      </td>
                      <td>{formatDateTime(capa.targetDate)}</td>
                      <td>{formatDateTime(capa.createdAt)}</td>
                    </tr>
                    {expandedCapa === capa.capaId && (
                      <tr className="task-row">
                        <td colSpan="8">
                          <div className="tasks-container">
                            <div className="tasks-header">
                              <h4>
                                Tasks ({capaTasks[capa.capaId]?.length || 0})
                              </h4>
                              <button
                                className="btn-icon"
                                onClick={() => setExpandedCapa(null)}
                                title="Collapse"
                              >
                                <HiOutlineXMark />
                              </button>
                            </div>
                            {capaTasks[capa.capaId] &&
                              capaTasks[capa.capaId].length > 0 ? (
                              <div className="tasks-list">
                                {capaTasks[capa.capaId].map((task) => (
                                  <div
                                    key={task.actionId}
                                    className="task-item-inline"
                                  >
                                    <div className="task-item-content">
                                      <div className="task-description">
                                        <strong>{task.taskDescription}</strong>
                                      </div>
                                      <div className="task-meta">
                                        <select
                                          className="status-select-inline task-status-select"
                                          value={task.statusId || ""}
                                          onChange={(e) =>
                                            handleUpdateTaskStatus(
                                              task.actionId,
                                              parseInt(e.target.value, 10),
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {taskStatuses.map((status) => (
                                            <option
                                              key={status.statusId}
                                              value={status.statusId}
                                            >
                                              {status.statusName}
                                            </option>
                                          ))}
                                        </select>
                                        <span className="priority-badge">
                                          {getPriorityLabel(task.priority)}
                                        </span>
                                        {task.dueDate && (
                                          <span>
                                            Due:{" "}
                                            {new Date(
                                              task.dueDate,
                                            ).toLocaleDateString()}
                                          </span>
                                        )}
                                        {task.assignedTo && (
                                          <span>
                                            Assigned:{" "}
                                            {getUserName(task.assignedTo)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="empty-tasks">
                                No tasks added yet.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="capa-cards-grid">
            {filteredCapaList.map((capa) => (
              <div key={capa.capaId} className="capa-card">
                <div className="capa-card-header">
                  <div className="capa-card-title">
                    <h4>{capa.title || "No Title"}</h4>
                    <div className="capa-card-badges">
                      <span className="type-pill">
                        {capa.capaType || "Corrective"}
                      </span>
                      {(capa.observationNumber ||
                        (capa.description &&
                          capa.description.match(/SBO-\d{4}-\d+/))) && (
                          <span
                            className="sbo-link-badge"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSboClick(capa);
                            }}
                          >
                            <HiOutlineShieldCheck size={12} />
                            {capa.observationNumber ||
                              (capa.description &&
                                capa.description.match(/SBO-\d{4}-\d+/)?.[0])}
                          </span>
                        )}
                      <span className="status-pill">
                        {getStatusName(capa.statusId)}
                      </span>
                    </div>
                  </div>
                  <div className="capa-card-actions">
                    {canEdit && (
                      <button
                        className="btn-icon-edit"
                        title="Open CAPA"
                        onClick={() =>
                          navigate(`/home/capa-entry/${capa.capaId}`)
                        }
                      >
                        <HiOutlinePencil />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="btn-icon-delete"
                        title="Delete CAPA"
                        onClick={() => setDeleteConfirm(capa)}
                      >
                        <HiOutlineTrash />
                      </button>
                    )}
                  </div>
                </div>
                <div className="capa-card-body">
                  <div className="capa-card-field">
                    <span className="field-label">Description:</span>
                    <span className="field-value">
                      {capa.description
                        ? capa.description.length > 150
                          ? `${capa.description.substring(0, 150)}...`
                          : capa.description
                        : "-"}
                    </span>
                  </div>
                  <div className="capa-card-field">
                    <span className="field-label">Linked RCA:</span>
                    <span className="field-value">
                      {capa.rcaId ? (
                        <button
                          className="link-button"
                          onClick={() =>
                            navigate(`/home/rca-entry/${capa.rcaId}`)
                          }
                        >
                          {getRcaTitle(capa.rcaId)}
                        </button>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="capa-card-field">
                    <span className="field-label">Tasks:</span>
                    <span className="field-value">
                      {capaTaskCounts[capa.capaId] ??
                        capaTasks[capa.capaId]?.length ??
                        0}
                    </span>
                  </div>
                  <div className="capa-card-footer">
                    <div className="capa-card-meta">
                      <span className="meta-item">
                        <strong>Target Date:</strong>{" "}
                        {formatDateTime(capa.targetDate)}
                      </span>
                      <span className="meta-item">
                        <strong>Created:</strong>{" "}
                        {formatDateTime(capa.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.totalPages > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      <SharedModal
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        title="Filters"
        footer={
          <>
            <button className="btn-secondary btn" onClick={clearFilters}>
              Clear
            </button>
            <button className="btn-primary btn" onClick={applyFilters}>
              Apply
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group">
            <label>Plant</label>
            <select
              name="plantId"
              value={filters.plantId}
              onChange={(e) => {
                  handleFilterChange(e);
                  if (e.target.value) localStorage.setItem('defaultPlantId', e.target.value);
              }}
            >
              <option value="">All Plants</option>
              {plants.map((plant) => (
                <option key={plant.plantId} value={plant.plantId}>
                  {plant.plantName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              name="statusId"
              value={filters.statusId}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              {statuses.map((status) => (
                <option key={status.statusId} value={status.statusId}>
                  {status.statusName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Module</label>
            <select
              name="module"
              value={filters.module || ""}
              onChange={handleFilterChange}
            >
              <option value="">All Modules</option>
              <option value="OPL Module">OPL Module</option>
              <option value="Kaizen Module">Kaizen Module</option>
              <option value="RCA Module">RCA Module</option>
              <option value="CAPA Module">CAPA Module</option>
              <option value="Safety Observation Module">
                Safety Observation Module
              </option>
              <option value="Work Order">Work Order</option>
              <option value="5S Audits">5S Audits</option>
              <option value="Enterprise ERP">Enterprise ERP</option>
              <option value="Incident Management">
                Incident Management
              </option>
              <option value="Work Permits">Work Permits</option>
            </select>
          </div>
        </div>
      </SharedModal>

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete CAPA"
        size="sm"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this CAPA record? This action
            cannot be undone.
          </p>
        </div>
      </SharedModal>

      {/* Task Modal */}
      <SharedModal
        isOpen={showTaskModal && !!selectedCapaForTask}
        onClose={() => setShowTaskModal(false)}
        title={`Add Task to ${selectedCapaForTask?.title}`}
        size="lg"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowTaskModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSaveTask}
              disabled={submittingTask}
            >
              {submittingTask ? "Saving..." : "Save Task"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group">
            <label htmlFor="taskDescription">
              Task Description <span className="required">*</span>
            </label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={taskFormData.taskDescription}
              onChange={handleTaskInputChange}
              className={taskFormErrors.taskDescription ? "error" : ""}
              rows={3}
              placeholder="Enter task description"
            />
            {taskFormErrors.taskDescription && (
              <span className="field-error">
                {taskFormErrors.taskDescription}
              </span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskAssignedTo">Assigned To</label>
              <select
                id="taskAssignedTo"
                name="assignedTo"
                value={taskFormData.assignedTo}
                onChange={handleTaskInputChange}
              >
                <option value="">Select User (Optional)</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.userName || `User ${user.userId}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taskDueDate">Due Date</label>
              <input
                type="date"
                id="taskDueDate"
                name="dueDate"
                value={taskFormData.dueDate}
                onChange={handleTaskInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskStatusId">
                Status <span className="required">*</span>
              </label>
              <select
                id="taskStatusId"
                name="statusId"
                value={taskFormData.statusId}
                onChange={handleTaskInputChange}
                className={taskFormErrors.statusId ? "error" : ""}
                disabled={taskStatuses.length === 0}
              >
                <option value="">
                  {taskStatuses.length === 0
                    ? "No statuses available"
                    : "Select Status"}
                </option>
                {taskStatuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName || `Status ${status.statusId}`}
                  </option>
                ))}
              </select>
              {taskFormErrors.statusId && (
                <span className="field-error">
                  {taskFormErrors.statusId}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="taskPriority">Priority</label>
              <select
                id="taskPriority"
                name="priority"
                value={taskFormData.priority}
                onChange={handleTaskInputChange}
              >
                <option value="1">High</option>
                <option value="2">Medium</option>
                <option value="3">Low</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="taskNotes">Notes</label>
            <textarea
              id="taskNotes"
              name="notes"
              value={taskFormData.notes}
              onChange={handleTaskInputChange}
              rows={3}
              placeholder="Enter task notes"
            />
          </div>
        </div>
      </SharedModal>

      {/* SBO Preview modal */}
      <ObservationPreview
        isOpen={isSboModalOpen}
        onClose={() => {
          setIsSboModalOpen(false);
          setSelectedSboRoundId(null);
          setSelectedCapaId(null);
        }}
        roundId={selectedSboRoundId}
      />
    </div>
  );
};

export default CAPA;
